import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Check, Link2, Unlink, RefreshCw, AlertCircle, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useLanguage } from '../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const STRINGS = {
  de: {
    loading: 'Shopify-Status wird geladen…',
    connectedAs: 'Verbunden mit',
    notConnected: 'Nicht verbunden',
    disconnect: 'Trennen',
    confirmDisconnect: 'Shopify-Verbindung wirklich trennen? Geplante Posts sind davon nicht betroffen.',
    disconnectedToast: 'Shopify-Verbindung getrennt.',
    disconnectFailed: 'Trennen fehlgeschlagen.',
    connectedToast: 'Shopify verbunden mit',
    connectionFailed: 'Verbindung fehlgeschlagen.',
    fillFields: 'Bitte alle Felder ausfüllen.',
    saved: 'Gespeichert.',
    saveFailed: 'Speichern fehlgeschlagen.',
    blogsLoaded: 'Blogs geladen.',
    blogsFailed: 'Blogs konnten nicht geladen werden.',
    intro: 'Verbinde deinen Shopify-Shop, um generierte Blog-Artikel direkt in deinen Shopify-Blog zu veröffentlichen.',
    steps: [
      'Shopify Dev Dashboard öffnen (dev.shopify.com) → App erstellen.',
      'In „Versionen“ die Scopes read_content,write_content eintragen und freigeben.',
      'App auf deinem Shop installieren.',
      'Unter „Einstellungen“ Client-ID und Client-Secret kopieren und hier einfügen.',
    ],
    modeCc: 'Dev-Dashboard-App (empfohlen)',
    modeToken: 'Bestehender Admin-API-Token',
    shop: 'Shop-Adresse',
    clientId: 'Client-ID',
    clientSecret: 'Client-Secret',
    token: 'Admin-API-Zugriffstoken (shpat_…)',
    connect: 'Verbinden',
    checking: 'Verbindung wird geprüft…',
    defaultBlog: 'Ziel-Blog',
    none: '— bitte wählen —',
    refresh: 'Aktualisieren',
    noBlogs: 'Keine Blogs gefunden. Lege in Shopify unter Onlineshop → Blogbeiträge einen Blog an und klicke „Aktualisieren“.',
    needBlog: 'Bitte einen Ziel-Blog wählen, sonst schlägt die Veröffentlichung fehl.',
    author: 'Autorenname',
  },
  en: {
    loading: 'Loading Shopify status…',
    connectedAs: 'Connected to',
    notConnected: 'Not connected',
    disconnect: 'Disconnect',
    confirmDisconnect: 'Really disconnect Shopify? Scheduled posts are unaffected.',
    disconnectedToast: 'Shopify disconnected.',
    disconnectFailed: 'Disconnect failed.',
    connectedToast: 'Shopify connected to',
    connectionFailed: 'Connection failed.',
    fillFields: 'Please fill in all fields.',
    saved: 'Saved.',
    saveFailed: 'Save failed.',
    blogsLoaded: 'blogs loaded.',
    blogsFailed: 'Could not load blogs.',
    intro: 'Connect your Shopify store to publish generated blog articles straight to your Shopify blog.',
    steps: [
      'Open the Shopify Dev Dashboard (dev.shopify.com) → create an app.',
      'Under “Versions” add the scopes read_content,write_content and release.',
      'Install the app on your store.',
      'Under “Settings” copy Client ID and Client secret and paste them here.',
    ],
    modeCc: 'Dev Dashboard app (recommended)',
    modeToken: 'Existing Admin API token',
    shop: 'Shop address',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    token: 'Admin API access token (shpat_…)',
    connect: 'Connect',
    checking: 'Testing connection…',
    defaultBlog: 'Target blog',
    none: '— please select —',
    refresh: 'Refresh',
    noBlogs: 'No blogs found. Create one in Shopify under Online Store → Blog posts, then click “Refresh”.',
    needBlog: 'Please select a target blog, otherwise publishing will fail.',
    author: 'Author name',
  },
};

/**
 * ShopifyCard — connect/disconnect a project's Shopify store and pick the
 * target blog. Mirrors WordPressCard (embedded in WebsiteIntegrationCard).
 */
export default function ShopifyCard({ getAuthHeader, embedded = false, onStatusChange }) {
  const { lang } = useLanguage();
  const t = STRINGS[lang === 'en' ? 'en' : 'de'];
  const [status, setStatus] = useState({ loading: true, connected: false, connection: null });
  const [mode, setMode] = useState('cc'); // 'cc' | 'token'
  const [shop, setShop] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [blogId, setBlogId] = useState('');
  const [author, setAuthor] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // See WordPressCard: keep the callback in a ref so `load` stays stable.
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
  const lastConnectedRef = useRef(null);

  const load = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/integrations/shopify/status`, { headers: getAuthHeader(), timeout: 8000 });
      const connected = !!data.connected;
      lastConnectedRef.current = connected;
      setStatus({ loading: false, connected, connection: data.connection || null });
      setBlogId(data.connection?.default_blog_id || '');
      setAuthor(data.connection?.author_name || '');
      onStatusChangeRef.current?.(connected);
    } catch (_e) {
      setStatus((s) => ({ ...s, loading: false }));
      onStatusChangeRef.current?.(lastConnectedRef.current ?? false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setConnectError('');
    const ready = shop.trim() && (mode === 'token' ? token.trim() : clientId.trim() && clientSecret.trim());
    if (!ready) {
      setConnectError(t.fillFields);
      return;
    }
    setConnecting(true);
    try {
      const body = mode === 'token'
        ? { shop: shop.trim(), access_token: token.trim() }
        : { shop: shop.trim(), client_id: clientId.trim(), client_secret: clientSecret.trim() };
      const { data } = await axios.post(`${API}/integrations/shopify/connect`, body, { headers: getAuthHeader(), timeout: 45000 });
      toast.success(`${t.connectedToast} ${data.connection?.shop_name || shop}.`);
      setShop(''); setClientId(''); setClientSecret(''); setToken('');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || t.connectionFailed;
      setConnectError(msg);
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t.confirmDisconnect)) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/integrations/shopify/disconnect`, {}, { headers: getAuthHeader() });
      toast.success(t.disconnectedToast);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.disconnectFailed);
    } finally {
      setDisconnecting(false);
    }
  };

  const saveSettings = async (patch, apply) => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/shopify/settings`, patch, { headers: getAuthHeader() });
      apply();
      toast.success(t.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshBlogs = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/integrations/shopify/blogs?refresh=true`, { headers: getAuthHeader(), timeout: 30000 });
      setStatus((s) => ({
        ...s,
        connection: s.connection ? { ...s.connection, blogs_cache: data.blogs || [] } : s.connection,
      }));
      toast.success(`${(data.blogs || []).length} ${t.blogsLoaded}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.blogsFailed);
    } finally {
      setRefreshing(false);
    }
  };

  if (status.loading) {
    return (
      <div className="bg-[#1A2B42] rounded-lg p-5 flex items-center gap-3" data-testid="shopify-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#00D9C8]" />
        <span className="text-sm text-[#A0AEC0]">{t.loading}</span>
      </div>
    );
  }

  const c = status.connection;
  const blogs = c?.blogs_cache || [];
  const inputCls = 'bg-[#0A1628] border-transparent text-white text-sm mt-1';

  return (
    <div className={embedded ? '' : 'bg-[#1A2B42] rounded-lg p-5'} data-testid="shopify-integration-card">
      <div className={`flex items-center justify-between ${embedded ? 'mb-3' : 'mb-4'}`}>
        <div>
          {!embedded && <h3 className="text-white font-medium">Shopify</h3>}
          {status.connected ? (
            <p className="text-xs flex items-center gap-1 text-emerald-400" data-testid="shopify-connection-status">
              <Check className="w-3 h-3" /> {t.connectedAs} <span className="font-medium">{c?.shop_name}</span>
            </p>
          ) : (
            <p className="text-xs text-[#5A6C7D]" data-testid="shopify-connection-status">{t.notConnected}</p>
          )}
        </div>
        {status.connected && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            data-testid="shopify-disconnect-btn"
          >
            {disconnecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Unlink className="w-3 h-3 mr-1" />}
            {t.disconnect}
          </Button>
        )}
      </div>

      {!status.connected && (
        <div className="space-y-3">
          <p className="text-xs text-[#A0AEC0] leading-relaxed">{t.intro}</p>
          <ol className="text-xs text-[#A0AEC0] list-decimal pl-5 space-y-0.5">
            {t.steps.map((s) => <li key={s}>{s}</li>)}
          </ol>

          <div className="flex gap-2 flex-wrap">
            {[['cc', t.modeCc], ['token', t.modeToken]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                className={`px-3 py-1.5 rounded border text-xs transition-all ${
                  mode === key ? 'bg-[#00D9C8]/10 border-[#00D9C8] text-white' : 'bg-[#0A1628] border-transparent text-[#A0AEC0]'
                }`}
                data-testid={`shopify-mode-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs text-[#A0AEC0]">{t.shop}</Label>
              <Input value={shop} onChange={(e) => setShop(e.target.value)} placeholder="meinshop.myshopify.com" className={inputCls} data-testid="shopify-shop-input" />
            </div>
            {mode === 'cc' ? (
              <>
                <div>
                  <Label className="text-xs text-[#A0AEC0]">{t.clientId}</Label>
                  <Input value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} data-testid="shopify-client-id-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#A0AEC0]">{t.clientSecret}</Label>
                  <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} className={inputCls} data-testid="shopify-client-secret-input" />
                </div>
              </>
            ) : (
              <div className="md:col-span-2">
                <Label className="text-xs text-[#A0AEC0]">{t.token}</Label>
                <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="shpat_…" className={inputCls} data-testid="shopify-token-input" />
              </div>
            )}
          </div>

          {connectError && (
            <div className="flex items-start gap-2 text-xs bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded px-3 py-2" data-testid="shopify-connect-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{connectError}</span>
            </div>
          )}

          <Button
            onClick={handleConnect}
            disabled={connecting}
            className="bg-[#5E8E3E] hover:bg-[#4d7633] text-white font-medium"
            data-testid="shopify-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
            {connecting ? t.checking : t.connect}
          </Button>
        </div>
      )}

      {status.connected && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-[#A0AEC0]">
            <ExternalLink className="w-3.5 h-3.5" />
            <a href={c?.primary_url} target="_blank" rel="noreferrer" className="text-[#00D9C8] hover:underline break-all">
              {c?.primary_url}
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs text-[#A0AEC0]">{t.defaultBlog}</Label>
              <select
                value={blogId}
                onChange={(e) => saveSettings({ blog_id: e.target.value || null }, () => setBlogId(e.target.value))}
                disabled={saving || blogs.length === 0}
                className="w-full mt-1 bg-[#0A1628] border border-transparent text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-[#00D9C8]"
                data-testid="shopify-default-blog-select"
              >
                <option value="">{t.none}</option>
                {blogs.map((b) => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshBlogs}
              disabled={refreshing}
              className="border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/10 h-9"
              data-testid="shopify-refresh-blogs-btn"
            >
              {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {t.refresh}
            </Button>
          </div>
          {blogs.length === 0 && <p className="text-xs text-amber-300/80">{t.noBlogs}</p>}
          {blogs.length > 0 && !blogId && <p className="text-xs text-amber-300/80">{t.needBlog}</p>}

          <div>
            <Label className="text-xs text-[#A0AEC0]">{t.author}</Label>
            <Input
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              onBlur={() => author.trim() && author !== c?.author_name && saveSettings({ author_name: author.trim() }, () => {})}
              className={inputCls}
              data-testid="shopify-author-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}
